import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaMapMarkerAlt, FaClock, FaStar, FaSpinner, FaChevronLeft, FaChevronRight, FaExclamationTriangle, FaCheck, FaInfoCircle } from 'react-icons/fa';
import toursService from '../../services/toursService';

const TourModal = ({ tourId, source = 'local', isOpen, onClose, fallbackTourData = null }) => {
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  // Debug log for Viator tour data
  useEffect(() => {
    if (tour && source === 'viator') {
      console.log('Viator tour data loaded:', tour);
      console.log('Tour ID:', tour._id);
      console.log('Viator product code:', tour.viatorData?.productCode);
      console.log('Tour price:', tour.price);
    }
  }, [tour, source]);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setActiveTab('details');
    } else {
      setTimeout(() => {
        setIsVisible(false);
      }, 300);
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchTourDetails = async () => {
      if (!tourId || !isOpen) return;
      
      const loadingTimeout = setTimeout(() => {
        if (loading) {
          console.warn('Loading timeout reached, using fallback data if available');
          if (fallbackTourData) {
            // Ensure fallback data has an ID
            const processedData = processTourData(fallbackTourData, source, tourId);
            setTour(processedData);
          } else {
            setError('Loading timed out. Please try again.');
          }
          setLoading(false);
        }
      }, 10000);
      
      try {
        setLoading(true);
        setError(null);
        console.log(`Fetching tour details for ID: ${tourId}, source: ${source}`);
        
        const tourData = await toursService.fetchTourById(tourId, source);
        console.log('Tour data received:', tourData);
        
        if (!tourData || Object.keys(tourData).length === 0) {
          throw new Error('Empty tour data received');
        }
        
        // Process the tour data to ensure it has an ID and preserve price info
        const processedData = processTourData(tourData, source, tourId);
        
        // If the price is missing or zero in fetched data but exists in fallback, use fallback price
        if (fallbackTourData && (!processedData.price || 
            (processedData.price.amount === 0 || processedData.price.amount === '0'))) {
          console.log('Using fallback tour price data');
          processedData.price = fallbackTourData.price;
        }
        
        setTour(processedData);
      } catch (err) {
        console.error('Error fetching tour details:', err);
        
        if (fallbackTourData) {
          console.log('Using fallback tour data');
          const processedData = processTourData(fallbackTourData, source, tourId);
          setTour(processedData);
        } else {
          setError(`Failed to load tour details: ${err.message || 'Unknown error'}`);
        }
      } finally {
        clearTimeout(loadingTimeout);
        setLoading(false);
      }
    };

    fetchTourDetails();
  }, [tourId, source, isOpen, fallbackTourData]);

  // Process tour data to ensure it has required fields
  const processTourData = (data, source, tourId) => {
    if (!data) return null;

    // Create a copy to avoid mutating the original
    const processedData = { ...data };

    // Ensure tour has an ID
    if (!processedData._id) {
      console.log('Adding ID to tour data');
      if (source === 'viator') {
        // For Viator tours, use the product code as ID
        processedData._id = processedData.viatorData?.productCode || `viator-${tourId}`;
        console.log('Generated Viator tour ID:', processedData._id);
      } else {
        processedData._id = `local-${tourId}`;
      }
    }

    // Ensure source is set
    if (!processedData.source) {
      processedData.source = source;
    }

    // Ensure tour has a name
    if (!processedData.name) {
      if (source === 'viator' && processedData.viatorData?.productCode) {
        processedData.name = `Tour ${processedData.viatorData.productCode}`;
      } else {
        processedData.name = `Tour ${processedData._id}`;
      }
      console.log('Generated tour name:', processedData.name);
    }

    // Ensure tour has a description
    if (!processedData.description || processedData.description.trim() === '') {
      processedData.description = 'Tour description will be updated soon.';
    }

    // Ensure price structure exists
    if (!processedData.price || processedData.price.amount === 0) {
      processedData.price = {
        amount: 0,
        currency: 'USD',
        per: 'person'
      };
    }

    // Log price for debugging
    if (processedData.price) {
      console.log('Tour price in processTourData:', processedData.price);
    } else {
      console.log('No price found in tour data');
    }

    return processedData;
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!isOpen && !isVisible) return null;

  // Format price with currency
  const formatPrice = (price) => {
    if (!price) return 'Price unavailable';
    
    // Make sure amount is a number and not zero
    const amount = typeof price.amount === 'number' ? price.amount : parseFloat(price.amount);
    
    if (isNaN(amount) || amount === 0) {
      // Check if there's a display price for Viator tours
      if (tour?.viatorData?.pricing?.displayPrice) {
        return tour.viatorData.pricing.displayPrice;
      }
      return 'Price unavailable';
    }
    
    return `${price.currency || '$'}${amount.toFixed(0)}`;
  };

  // Format location
  const formatLocation = (location) => {
    if (!location) return 'Location unavailable';
    if (typeof location === 'string') return location;
    
    const address = location.address || {};
    const parts = [
      address.street,
      address.city,
      address.state,
      address.country
    ].filter(Boolean);
    
    if (parts.length > 0) return parts.join(', ');
    if (location.meetingPoint) return location.meetingPoint;
    
    return 'Location unavailable';
  };

  // Format duration
  const formatDuration = (duration) => {
    if (!duration) return 'Duration unavailable';
    if (typeof duration === 'string') return duration;
    
    const { value, unit } = duration;
    if (!value) return 'Duration unavailable';
    
    return `${value} ${unit || 'hours'}`;
  };

  // Calculate average rating
  const calculateRating = (reviews) => {
    if (!reviews || reviews.length === 0) {
      if (tour?.rating?.average) {
        return parseFloat(tour.rating.average).toFixed(1);
      }
      return 0;
    }
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  const nextImage = () => {
    if (!tour?.images?.gallery?.length) return;
    setCurrentImageIndex((prevIndex) => 
      prevIndex === tour.images.gallery.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    if (!tour?.images?.gallery?.length) return;
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? tour.images.gallery.length - 1 : prevIndex - 1
    );
  };

  // Get current image
  const getCurrentImage = () => {
    if (!tour) return null;
    
    if (tour.images?.gallery?.length > 0 && currentImageIndex < tour.images.gallery.length) {
      return tour.images.gallery[currentImageIndex];
    }
    
    return tour.images?.main || 'https://placehold.co/800x600/1A1B1E/ffffff?text=No+Image';
  };

  // Get booking URL for Viator tours
  const getBookingUrl = () => {
    if (source === 'viator' && tour?.viatorData?.deepLink) {
      return tour.viatorData.deepLink;
    }
    return tour?.bookingUrl || null;
  };

  // Handle booking action
  const handleBooking = () => {
    const bookingUrl = getBookingUrl();
    if (bookingUrl) {
      console.log('Opening booking URL:', bookingUrl);
      window.open(bookingUrl, '_blank');
    } else {
      console.log('No booking URL available for tour:', tour._id);
      alert(`Booking for "${tour.name}" will be implemented soon!`);
    }
  };

  // Render review stars
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<FaStar key={i} className="text-yellow-500" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<FaStar key={i} className="text-yellow-300" />);
      } else {
        stars.push(<FaStar key={i} className="text-gray-300" />);
      }
    }
    
    return <div className="flex">{stars}</div>;
  };

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black transition-opacity duration-300 ${isVisible ? 'bg-opacity-70 opacity-100' : 'bg-opacity-0 opacity-0'}`}
      onClick={handleClose}
    >
      <div 
        className={`relative bg-white rounded-lg shadow-xl w-full h-full md:w-4/5 max-w-4xl md:max-h-[90vh] transition-transform duration-300 ${isVisible ? 'scale-100' : 'scale-95'}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64 p-6">
            <FaSpinner className="animate-spin text-indigo-600 text-3xl mb-4" />
            <p className="text-gray-600">Loading tour details...</p>
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <FaExclamationTriangle className="text-red-500 text-3xl mx-auto mb-4" />
            <p className="text-red-500 mb-2 font-medium">Error Loading Tour</p>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={handleClose}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            >
              Close
            </button>
          </div>
        ) : tour ? (
          <>
            {/* Scrollable container for everything except footer */}
            <div 
              className="absolute top-0 bottom-[76px] left-0 right-0 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              style={{overscrollBehavior: 'contain'}}
            >
              {/* Close button - fixed to top right */}
              <button 
                onClick={handleClose}
                className="fixed top-4 right-4 z-20 bg-white rounded-full p-2 shadow-md hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <FaTimes className="text-gray-800" />
              </button>
              
              {/* Image gallery - part of scrollable content */}
              <div className="w-full h-64 sm:h-80 md:h-96 bg-gray-100 relative">
                <img 
                  src={getCurrentImage()} 
                  alt={tour.name} 
                  className="w-full h-full object-cover"
                />
                
                {tour.images?.gallery?.length > 1 && (
                  <>
                    <button 
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 rounded-full p-2 shadow-md hover:bg-opacity-100 transition-all z-10"
                      aria-label="Previous image"
                    >
                      <FaChevronLeft className="text-gray-800" />
                    </button>
                    <button 
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 rounded-full p-2 shadow-md hover:bg-opacity-100 transition-all z-10"
                      aria-label="Next image"
                    >
                      <FaChevronRight className="text-gray-800" />
                    </button>
                    
                    {/* Image counter */}
                    <div className="absolute bottom-4 right-4 bg-black bg-opacity-60 text-white px-2 py-1 rounded-md text-sm z-10">
                      {currentImageIndex + 1} / {tour.images.gallery.length}
                    </div>
                  </>
                )}
                
                {/* Price badge */}
                {tour.price && (
                  <div className="absolute top-4 left-4 bg-white text-gray-900 px-3 py-1 rounded-md text-lg font-medium shadow-md z-10">
                    {formatPrice(tour.price)}
                    {tour.price.per && <span className="text-xs ml-1">/{tour.price.per}</span>}
                  </div>
                )}
              </div>
              
              {/* All content below image - part of same scrollable container */}
              <div>
                {/* Header */}
                <div className="p-5 border-b border-gray-200 bg-white">
                  <h2 className="text-xl sm:text-2xl font-medium text-gray-900 mb-3">{tour.name}</h2>
                  
                  <div className="flex flex-wrap items-center gap-4 mb-3 text-sm text-gray-600">
                    {tour.location && (
                      <div className="flex items-center">
                        <FaMapMarkerAlt className="mr-1.5 text-gray-500" />
                        <span>{formatLocation(tour.location)}</span>
                      </div>
                    )}
                    
                    {tour.duration && (
                      <div className="flex items-center">
                        <FaClock className="mr-1.5 text-gray-500" />
                        <span>{formatDuration(tour.duration)}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Rating */}
                  {(tour.reviews?.length > 0 || tour.rating) && (
                    <div className="flex items-center gap-2">
                      {renderStars(calculateRating(tour.reviews))}
                      <span className="text-sm text-gray-600">
                        {calculateRating(tour.reviews)} 
                        {tour.reviews?.length > 0 && ` (${tour.reviews.length} reviews)`}
                        {!tour.reviews?.length && tour.rating?.count > 0 && ` (${tour.rating.count} reviews)`}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Tabs */}
                <div className="border-b border-gray-200 bg-white sticky top-0 z-10">
                  <div className="flex px-5">
                    <button
                      className={`py-3 text-sm font-medium relative ${activeTab === 'details' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                      onClick={() => setActiveTab('details')}
                    >
                      <span className="px-4">Details</span>
                      {activeTab === 'details' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900"></span>}
                    </button>
                    <button
                      className={`py-3 text-sm font-medium relative ${activeTab === 'requirements' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                      onClick={() => setActiveTab('requirements')}
                    >
                      <span className="px-4">Requirements</span>
                      {activeTab === 'requirements' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900"></span>}
                    </button>
                    <button
                      className={`py-3 text-sm font-medium relative ${activeTab === 'cancellation' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                      onClick={() => setActiveTab('cancellation')}
                    >
                      <span className="px-4">Cancellation</span>
                      {activeTab === 'cancellation' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900"></span>}
                    </button>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-5 pb-8">
                  {activeTab === 'details' && (
                    <div>
                      <p className="text-gray-700 leading-relaxed">{tour.description}</p>
                      {tour.detailed_description && tour.detailed_description !== tour.description && (
                        <p className="mt-4 text-gray-700 leading-relaxed">{tour.detailed_description}</p>
                      )}
                      
                      {/* Viator specific details */}
                      {source === 'viator' && tour.viatorData && (
                        <div className="mt-5 p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-3">Additional Information</h4>
                          
                          {tour.viatorData.productOptions && tour.viatorData.productOptions.length > 0 && (
                            <div className="mb-4">
                              <p className="text-sm font-medium text-gray-700 mb-2">Available Options:</p>
                              <div className="space-y-3">
                                {tour.viatorData.productOptions.map((option, index) => (
                                  <div key={index} className="text-sm">
                                    <p className="font-medium text-gray-700">{option.title}</p>
                                    {option.description && (
                                      <p className="text-gray-600 mt-1">{option.description.replace(/<br\s*\/?>/gi, ' ')}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {tour.viatorData.languages && tour.viatorData.languages.length > 0 && (
                            <div className="mb-3">
                              <p className="text-sm font-medium text-gray-700 mb-2">Languages:</p>
                              <p className="text-sm text-gray-600">
                                {tour.viatorData.languages.map(lang => lang.code.toUpperCase()).join(', ')}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {activeTab === 'requirements' && (
                    <div>
                      {tour.requirements ? (
                        <>
                          {tour.requirements.minimum_age && (
                            <div className="mb-4">
                              <p className="text-sm font-medium text-gray-700 mb-1">Minimum Age:</p>
                              <p className="text-gray-600">{tour.requirements.minimum_age} years</p>
                            </div>
                          )}
                          
                          {tour.requirements.physical_level && (
                            <div className="mb-4">
                              <p className="text-sm font-medium text-gray-700 mb-1">Physical Level:</p>
                              <p className="text-gray-600">{tour.requirements.physical_level}</p>
                            </div>
                          )}
                          
                          {tour.requirements.prerequisites && tour.requirements.prerequisites.length > 0 && (
                            <div className="mb-4">
                              <p className="text-sm font-medium text-gray-700 mb-2">Prerequisites:</p>
                              <div className="space-y-3">
                                {tour.requirements.prerequisites.map((item, index) => (
                                  <p key={index} className="text-gray-600">{item}</p>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {tour.requirements.toBring && tour.requirements.toBring.length > 0 && (
                            <div className="mb-4">
                              <p className="text-sm font-medium text-gray-700 mb-2">What to Bring:</p>
                              <div className="space-y-3">
                                {tour.requirements.toBring.map((item, index) => (
                                  <p key={index} className="text-gray-600">{item}</p>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {tour.requirements.provided && tour.requirements.provided.length > 0 && (
                            <div className="mb-4">
                              <p className="text-sm font-medium text-gray-700 mb-2">What's Included:</p>
                              <div className="space-y-3">
                                {tour.requirements.provided.map((item, index) => (
                                  <p key={index} className="text-gray-600">{item}</p>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-gray-500 italic">No specific requirements provided for this tour.</p>
                      )}
                    </div>
                  )}
                  
                  {activeTab === 'cancellation' && (
                    <div>
                      {tour.cancellation ? (
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-start">
                            <div className="mr-3 mt-0.5">
                              {tour.cancellation.refundable ? (
                                <FaCheck className="text-green-500" />
                              ) : (
                                <FaTimes className="text-red-500" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {tour.cancellation.refundable ? 'Refundable' : 'Non-refundable'}
                              </p>
                              <p className="text-gray-600 mt-2 leading-relaxed">
                                {tour.cancellation.policy || (tour.cancellation.refundable 
                                  ? `Free cancellation up to ${tour.cancellation.deadline || 24} hours before the experience`
                                  : 'This experience cannot be cancelled or refunded'
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500 italic">No cancellation policy provided for this tour.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Footer - fixed at bottom */}
            <div className="absolute bottom-0 left-0 right-0 p-5 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row gap-3">
                <button 
                  onClick={handleClose}
                  className="flex-1 py-3 bg-white border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Close
                </button>
                <button 
                  className="flex-1 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                  onClick={handleBooking}
                >
                  Book Now
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <FaInfoCircle className="text-gray-400 text-3xl mx-auto mb-4" />
            <p>Tour not found</p>
            <button 
              onClick={handleClose}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TourModal; 